import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';
import { useAnnee } from './useAnnee';
import { useRessource } from './useRessource';
import { api } from '../lib/apiClient';

/**
 * Le COMPTEUR D'ANOMALIES du menu, partage.
 *
 * Il vivait dans le bandeau, charge une fois : traiter une anomalie ne
 * le faisait pas bouger, et la pastille annonçait un chiffre perime
 * jusqu'au prochain rechargement de la page.
 *
 * Deux moments le rafraichissent :
 *   - CHANGER D'ECRAN. Une anomalie se corrige rarement depuis l'ecran
 *     qui la liste — on complete une date de naissance sur la fiche, on
 *     rattache un creneau depuis les inscriptions. Recompter a chaque
 *     navigation attrape tous ces cas sans que chaque ecran ait a y
 *     penser ; la requete est courte, et une par navigation est peu ;
 *   - un appel explicite a `rafraichir()`, pour l'ecran Anomalies qui,
 *     lui, agit sans changer de page (lever une exception, la retirer).
 *
 * Le compteur suit l'annee du bandeau, comme l'ecran qu'il annonce : les
 * exceptions sont rattachees a une annee.
 */

interface Anomalies {
  nombre: number;
  rafraichir: () => void;
}

const Contexte = createContext<Anomalies>({ nombre: 0, rafraichir: () => undefined });

export function FournisseurAnomalies({ children }: { children: ReactNode }) {
  const { peut, entiteActive } = useAuth();
  const annee = useAnnee();
  const emplacement = useLocation();

  const autorise = peut('personne.lire', 'tableau_bord.lire');

  const { donnees, recharger } = useRessource(
    () =>
      autorise && annee.anneeId
        ? api.get<{ nombre: number }[]>('/api/anomalies', { anneeId: annee.anneeId })
        : Promise.resolve([]),
    [autorise, annee.anneeId, entiteActive?.id],
  );

  // Le changement d'ecran vaut recomptage : ce qu'on vient de corriger
  // ailleurs se voit en arrivant.
  useEffect(() => {
    if (autorise) recharger();
    // `recharger` change a chaque rendu de `useRessource` : le suivre
    // relancerait la requete en boucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emplacement.pathname]);

  const nombre = (donnees ?? []).reduce((somme, ligne) => somme + ligne.nombre, 0);

  return <Contexte.Provider value={{ nombre, rafraichir: recharger }}>{children}</Contexte.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAnomalies(): Anomalies {
  return useContext(Contexte);
}
